/**
 * Client-side profanity filtering for player names (SPEC §2.1) and generated
 * room codes (SPEC §5.1). The server validates independently — this exists so
 * the user gets an instant "pick something else" rather than a round-trip, and
 * so a generated room code is never embarrassing.
 *
 * Deliberately small and conservative. An aggressive list breaks real names
 * (the Scunthorpe problem), and this is a party game among friends, not a
 * public forum. Substring matching is only applied to a short list of terms
 * that cannot appear innocently inside a 12-character first name.
 */

/** Matched anywhere in the string. Kept short to avoid false positives. */
const SUBSTRING_TERMS: readonly string[] = [
  'fuck',
  'shit',
  'cunt',
  'nigger',
  'nigga',
  'faggot',
  'retard',
  'rapist',
  'wanker',
  'bollock',
  'bastard',
  'whore',
  'slut',
  'dickhead',
  'arsehole',
  'asshole',
  'pedo',
  'paedo',
  'nonce',
];

/** Matched only as a whole word/token. Short or otherwise collision-prone. */
const EXACT_TERMS: readonly string[] = [
  'ass',
  'anal',
  'anus',
  'arse',
  'clit',
  'cock',
  'coon',
  'crap',
  'cum',
  'damn',
  'dick',
  'dildo',
  'fag',
  'gash',
  'jizz',
  'knob',
  'minge',
  'muff',
  'piss',
  'poo',
  'prick',
  'pube',
  'pussy',
  'queer',
  'rape',
  'scum',
  'semen',
  'sex',
  'shag',
  'spunk',
  'tit',
  'tits',
  'titty',
  'turd',
  'twat',
  'vag',
  'wank',
];

/**
 * Leetspeak / homoglyph normalisation. `n1gg3r` and `f*ck` should not walk
 * straight past the filter.
 */
const SUBSTITUTIONS: Readonly<Record<string, string>> = {
  '0': 'o',
  '1': 'i',
  '3': 'e',
  '4': 'a',
  '5': 's',
  '7': 't',
  '8': 'b',
  '@': 'a',
  $: 's',
  '!': 'i',
  '+': 't',
  '*': '',
  '.': '',
  '-': '',
  _: '',
  ' ': '',
};

function normalise(input: string): string {
  const lowered = input
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '');
  let out = '';
  for (const char of lowered) {
    const mapped = SUBSTITUTIONS[char];
    out += mapped === undefined ? char : mapped;
  }
  // Collapse runs ("fuuuuck" -> "fuck") without destroying legitimate doubles.
  return out.replace(/([a-z])\1{2,}/g, '$1');
}

/** True when the text contains something we would rather not put on a card. */
export function isProfane(input: string): boolean {
  const normalised = normalise(input);
  if (normalised.length === 0) return false;

  for (const term of SUBSTRING_TERMS) {
    if (normalised.includes(term)) return true;
  }

  // Exact terms: compare against the whole normalised token, and against the
  // raw lower-cased word boundaries of the original.
  if (EXACT_TERMS.includes(normalised)) return true;
  for (const word of input.toLowerCase().split(/[^a-z0-9]+/)) {
    if (word && EXACT_TERMS.includes(normalise(word))) return true;
  }

  return false;
}

/** True when a generated 4-letter room code should be thrown away and re-rolled. */
export function isProfaneRoomCode(code: string): boolean {
  return isProfane(code);
}
